export type TextureState = 'waiting' | 'active' | 'verified' | 'complete';

export type WarpedTexturePreset =
  | TextureState
  | 'assistantBlue'
  | 'assistantGreen'
  | 'assistantDark'
  | 'proactiveSlate';

type TexturePreset = {
  /** CSS fallback shown until WebGL paints its first frame. */
  background: string;
  colors: readonly [string, string, string, string, string];
  positions: number;
  waveX: number;
  waveXShift: number;
  waveY: number;
  waveYShift: number;
  mixing: number;
  grainMixer: number;
  grainOverlay: number;
};

export const TEXTURE_PRESETS: Record<WarpedTexturePreset, TexturePreset> = {
  waiting: {
    background:
      'radial-gradient(circle at 72% 72%, rgba(212, 212, 216, 0.14), transparent 34%), linear-gradient(145deg, #16171a 0%, #3f4248 100%)',
    colors: ['#111216', '#24262c', '#454a53', '#979da6', '#e4e4e7'],
    positions: 0.54,
    waveX: 0.38,
    waveXShift: 0.22,
    waveY: 0.36,
    waveYShift: 0.68,
    mixing: 0.62,
    grainMixer: 0.3,
    grainOverlay: 0.28,
  },
  active: {
    background:
      'radial-gradient(circle at 72% 72%, rgba(216, 226, 235, 0.46), transparent 34%), linear-gradient(145deg, #07101c 0%, #496c91 100%)',
    colors: ['#07101c', '#18304d', '#496c91', '#d8e2eb', '#f6f3ed'],
    positions: 0.44,
    waveX: 0.18,
    waveXShift: 0.72,
    waveY: 0.62,
    waveYShift: 0.16,
    mixing: 0.72,
    grainMixer: 0.3,
    grainOverlay: 0.4,
  },
  verified: {
    background:
      'radial-gradient(circle at 72% 72%, rgba(188, 216, 178, 0.42), transparent 34%), linear-gradient(145deg, #1b2618 0%, #2d796b 100%)',
    colors: ['#1b2618', '#12372f', '#2d796b', '#7ea873', '#ecf1df'],
    positions: 0.64,
    waveX: 0.7,
    waveXShift: 0.1,
    waveY: 0.24,
    waveYShift: 0.78,
    mixing: 0.56,
    grainMixer: 0.3,
    grainOverlay: 0.32,
  },
  complete: {
    background:
      'radial-gradient(circle at 72% 72%, rgba(188, 216, 178, 0.42), transparent 34%), linear-gradient(145deg, #1b2618 0%, #2d796b 100%)',
    colors: ['#1b2618', '#12372f', '#2d796b', '#7ea873', '#ecf1df'],
    positions: 0.64,
    waveX: 0.7,
    waveXShift: 0.1,
    waveY: 0.24,
    waveYShift: 0.78,
    mixing: 0.56,
    grainMixer: 0.3,
    grainOverlay: 0.32,
  },
  /** Applied Assistant: Weekly CX Brief / connected-system research. */
  assistantBlue: {
    background:
      'radial-gradient(circle at 16% 18%, rgba(247, 247, 244, 0.62) 0%, transparent 34%), radial-gradient(circle at 82% 72%, rgba(95, 158, 154, 0.36) 0%, transparent 44%), linear-gradient(145deg, #f7f7f4 0%, #dfe8e5 34%, #a9c9c7 62%, #5f9e9a 78%, #162235 100%)',
    colors: ['#f7f7f4', '#dfe8e5', '#a9c9c7', '#5f9e9a', '#162235'],
    positions: 0.32,
    waveX: 0.32,
    waveXShift: 0.2,
    waveY: 0.54,
    waveYShift: 0.3,
    mixing: 0.62,
    grainMixer: 0.22,
    grainOverlay: 0.15,
  },
  /** Applied Assistant: company memory and learned preferences. */
  assistantGreen: {
    background:
      'radial-gradient(circle at 18% 76%, rgba(120, 148, 108, 0.32) 0%, transparent 38%), radial-gradient(circle at 82% 18%, rgba(169, 201, 199, 0.36) 0%, transparent 42%), linear-gradient(132deg, #f7f7f4 0%, #dfe8e5 34%, #b9cba8 62%, #78946c 80%, #162235 100%)',
    colors: ['#f7f7f4', '#dfe8e5', '#b9cba8', '#78946c', '#162235'],
    positions: 0.34,
    waveX: 0.56,
    waveXShift: 0.16,
    waveY: 0.3,
    waveYShift: 0.7,
    mixing: 0.58,
    grainMixer: 0.2,
    grainOverlay: 0.15,
  },
  /** Applied Assistant: access policies, approvals, and audit trails. */
  assistantDark: {
    background:
      'radial-gradient(circle at 18% 18%, rgba(247, 247, 244, 0.18) 0%, transparent 34%), radial-gradient(circle at 76% 74%, rgba(95, 158, 154, 0.34) 0%, transparent 42%), linear-gradient(145deg, #08111d 0%, #162235 42%, #3f5d68 72%, #a9c9c7 92%, #f7f7f4 100%)',
    colors: ['#08111d', '#162235', '#3f5d68', '#a9c9c7', '#f7f7f4'],
    positions: 0.42,
    waveX: 0.54,
    waveXShift: 0.22,
    waveY: 0.48,
    waveYShift: 0.16,
    mixing: 0.7,
    grainMixer: 0.24,
    grainOverlay: 0.18,
  },
  /** Applied Proactive Agent: scheduled and real-time multi-channel launch. */
  proactiveSlate: {
    background:
      'radial-gradient(circle at 18% 78%, rgba(247, 247, 244, 0.46) 0%, transparent 36%), radial-gradient(circle at 82% 18%, rgba(70, 86, 110, 0.36) 0%, transparent 38%), linear-gradient(132deg, #f4f4f0 0%, #cfd4d9 38%, #758294 68%, #28364b 100%)',
    colors: ['#f7f7f4', '#cfd4d9', '#96a0ab', '#53657c', '#26354b'],
    positions: 0.34,
    waveX: 0.62,
    waveXShift: 0.12,
    waveY: 0.28,
    waveYShift: 0.72,
    mixing: 0.56,
    grainMixer: 0.18,
    grainOverlay: 0.14,
  },
};
